import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { RightChevron } from '@/assets/icons/right';
import { PaymentLogo } from '@/components/PaymentLogo';
import { RadioItem } from '@/components/RadioItem';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { useNonWithdrawableVerificationCheck } from '@/feature/account-details/hooks/use-non-withdrawable-verification-check';
import { useModals } from '@/feature/alerts/hooks/use-modals';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { ExpiredCardTag } from '@/feature/deposit/components/ExpiredCardTag';
import { useSelectedWithdrawalMethodStore } from '@/feature/withdraw/hooks/use-selected-withdrawal-method';
import { WithdrawStackNavigationProp } from '@/navigation/withdraw/types';
import { common } from '@/styles/styles';
import { runAfterInteractions } from '@/utils/runAfterInteractions';
import { toTitleCase } from '@/utils/toTitleCase';

import { WithdrawMethod } from '../hooks/use-withdrawal-methods';
import { formatWithdrawalMethodDisplay, getWithdrawalMethodName } from '../utils/utils';

export const styles = StyleSheet.create({
    listItem: {
        display: 'flex',
        columnGap: 8,
        minHeight: 96,
        paddingVertical: 16,
    },
});

type WithdrawalMethodItemProps = {
    method: WithdrawMethod;
    methods: WithdrawMethod[];
};

/**
 * Withdrawal method item
 * */
export const WithdrawalMethodItem = ({ method, methods }: WithdrawalMethodItemProps) => {
    const navigation = useNavigation<WithdrawStackNavigationProp>();
    const { dismissAll } = useModals();
    const setSelectedMethod = useSelectedWithdrawalMethodStore(state => state.setSelectedMethod);
    const selectedMethod = useSelectedWithdrawalMethodStore(state => state.selectedWithdrawalMethod);
    const { isUserLightSdd } = useNonWithdrawableVerificationCheck();

    const handlePress = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.SELECT_WITHDRAW_METHOD, { method: method?.type });
        const route = method.is_expired ? 'WithdrawalExpiryUpdate' : 'WithdrawForm';
        setSelectedMethod(method);
        navigation.navigate(route, {
            selectedMethod: method,
            methods,
        });

        runAfterInteractions(dismissAll);
    };

    return (
        <TouchableOpacity
            style={[styles.listItem, common.row, common.alignCenter, common.justifySpaceBetween]}
            onPress={handlePress}
            testID={method.type}
        >
            <Box flexDirection="row" flex={1} alignItems={'center'}>
                <PaymentLogo source={method?.payment_logos} />
                <Box flex={1}>
                    <Text fontSize={15}>{getWithdrawalMethodName(method.type, method?.cc_type ?? '')}</Text>
                    <Box>
                        <Text color="neutral300" fontSize={13} numberOfLines={1}>
                            {formatWithdrawalMethodDisplay(method.type, method?.cc_last4, method?.venmo_id)}{' '}
                            {method.via_vertical_name ? `(via ${toTitleCase(method.via_vertical_name)})` : ''}
                        </Text>

                        {method.closed_loop_remain > 0 && !isUserLightSdd ? (
                            <>
                                <Text color="neutral300" fontSize={13}>
                                    Required distribution: ${method.closed_loop_remain}
                                </Text>
                            </>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            {method.is_expired ? (
                <>
                    <Box mr={'s8'}>
                        <ExpiredCardTag />
                    </Box>
                    <Box justifyContent="center" alignItems="flex-end">
                        <RightChevron />
                    </Box>
                </>
            ) : (
                <RadioItem selected={method === selectedMethod} />
            )}
        </TouchableOpacity>
    );
};
